/**
 * JobAutomation: the Settings tab's "Automation" card (admins only). Three
 * switches for the job, each with one plain line:
 *
 *   Process this job's captures            the processing queue's job list
 *                                          (the Processing tab's switch: one list)
 *   Require my approval before guests get anything   delivery.requireReview (on by default)
 *   Send guests their email automatically after approval   delivery.autoSend (off by default)
 *
 * The card shows the switches as the queue and guest delivery read them
 * (getJobAutomation) and changes them only through setJobAutomation (admin
 * only; the server records who changed what and when, and refuses unsafe
 * combinations). The browser never writes these fields.
 *
 * Safety: the review off needs the typed phrase "turn off review"; auto-send
 * on with the review off (in either order) shows a strong warning and needs
 * "send without review" too; the server checks both phrases. While the job
 * is switched on for processing (or a capture of it is being processed), the
 * review stays on: the switch says so instead of asking. Auto-send may be on
 * with processing, as long as the review is on.
 *
 * A client job also has "Previews" (SafeShare slice 4.3): "Automate previews"
 * (delivery.autoPreviews; off by default: then no preview is made unless you
 * press "Make previews now", and those wait for your approval; on, every
 * verified capture gets its previews, approved automatically, the captures
 * already in the cloud included), "Make previews now" (makePreviews), and the
 * client emails to tell about previews, chosen from the job's client contacts
 * (delivery.previewRecipients; the server checks each one).
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import "./rigv2-review.css";
import { rigv2 } from "./rigv2Callables.js";
import { useModalDialog } from "./dialogFocus.js";

export const REVIEW_OFF_PHRASE = "turn off review";
export const SEND_WITHOUT_REVIEW_PHRASE = "send without review";

export const SETTINGS = [
  { id: "processing", label: "Process this job's captures",
    hint: "The processing computers make each capture's 3D model. It needs your approval on." },
  { id: "requireReview", label: "Require my approval before guests get anything",
    hint: "Each model waits on the Review tab until you approve it, or choose photos only. On unless you turn it off." },
  { id: "autoSend", label: "Send guests their email automatically after approval",
    hint: "When you approve a capture, everyone in it is emailed at once, without pressing Send. Off unless you turn it on." },
];

const typedOk = (s, phrase) => String(s ?? "").trim().toLowerCase() === phrase;
export const valueOf = (state, id) => (id === "processing" ? state.processing : id === "requireReview" ? state.reviewOn
  : id === "autoPreviews" ? Boolean(state.autoPreviewsOn) : state.autoSendOn);

/**
 * Why a switch cannot be flipped now, or "" (the server refuses the same):
 * processing cannot go on with the review off; the review
 * cannot go off while processing is on for the job or one of its captures is
 * being processed. Automatic emails may be on with processing (Ivin,
 * 2026-10-01): they send only approved models.
 */
export function blockedReason(state, id) {
  const on = valueOf(state, id);
  if (id === "processing" && !on) {
    if (!state.reviewOn) return "Turn your approval on first: with it off, a model would reach guests without you.";
    return "";
  }
  // Only the approval off is blocked by processing; automatic emails may be on (they send only approved models).
  if (!(id === "requireReview" && on)) return "";
  if (state.processing) return "Processing is on for this job: switch it off first. Your approval stays on while it is.";
  if (state.processingNow) return "A capture of this job is still being processed: wait until it is done, or hold it on the Processing tab.";
  return "";
}

/**
 * What flipping a switch asks first: null (it goes at once: the safe way),
 * "confirm" (a plain yes), "review_off" (the typed phrase, and the second
 * one when auto-send is on), "send_without_review" (the strong warning and
 * the second phrase: auto-send on with the review off).
 */
export function askFor(state, id) {
  const on = valueOf(state, id);
  if (id === "processing") return on ? null : "confirm";
  if (id === "autoPreviews") return on ? null : "confirm";
  if (id === "requireReview") return on ? "review_off" : null;
  if (on) return null;
  return state.reviewOn ? "confirm" : "send_without_review";
}

const shortTime = (ms) => (ms ? new Date(ms).toLocaleString("en-US", {
  month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZone: "America/New_York",
}) : "");
const SETTING_WORDS = { processing: "processing", requireReview: "approval before guests get anything", autoSend: "automatic emails",
  autoPreviews: "automatic previews" };

const REFUSAL_WORDS = {
  kinds_off: "previews are not switched on in the processing queue yet",
  no_previews: "no preview fits its preset",
  classes_dropped: "the client keeps no previews",
  no_photos: "its photos are not in the cloud",
};

/** What a previews pass did, in one line: "Previews started for 3 captures; 17 had theirs already." */
export function previewsLine(r) {
  if (!r) return "";
  const parts = [];
  parts.push(r.created === 1 ? "Previews started for 1 capture" : `Previews started for ${r.created ?? 0} captures`);
  if (r.existing) parts.push(`${r.existing} had theirs already`);
  const refused = Object.entries(r.refused || {}).reduce((n, [, c]) => n + c, 0);
  if (refused) parts.push(`${refused} could not get one (${Object.keys(r.refused).map((k) => REFUSAL_WORDS[k] ?? k).join("; ")})`);
  if (r.failed) parts.push(`${r.failed} failed (try again)`);
  if (r.switchedOff) parts.push("the switch was turned off meanwhile");
  return `${parts.join("; ")}.${r.more ? " There are more: press Make previews now again." : ""}`;
}
/** One line of the history: "Oct 1, 3:40 PM: ivin@… turned automatic emails on (Settings)." */
export const historyLine = (h) =>
  `${shortTime(h.at)}: ${h.byEmail || "an admin"} turned ${SETTING_WORDS[h.setting] ?? h.setting} ${h.to ? "on" : "off"} (${h.via === "setProcessingJobAllowed" ? "Processing tab" : "Settings"}).`;

function Dialog({ label, onClose, children }) {
  const frameRef = useRef(null);
  useModalDialog(frameRef);
  return (
    <div className="rigv2-modal-scrim rv-scrim" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="rv-dialog" role="dialog" aria-modal="true" aria-label={label} tabIndex={-1} ref={frameRef}
        onKeyDown={(e) => { if (e.key === "Escape") onClose(); }}>
        {children}
      </div>
    </div>
  );
}

/** The confirmation for one switch (pure props: exported for the test). */
export function AutomationDialog({ kind, setting, state, busy = false, onConfirm, onClose }) {
  const [first, setFirst] = useState("");
  const [second, setSecond] = useState("");
  const needsSecond = kind === "send_without_review" || (kind === "review_off" && state.autoSendOn);
  const needsFirst = kind === "review_off";
  const ready = (!needsFirst || typedOk(first, REVIEW_OFF_PHRASE)) && (!needsSecond || typedOk(second, SEND_WITHOUT_REVIEW_PHRASE));
  const title = setting === "processing" ? "Process this job's captures?"
    : setting === "autoPreviews" ? "Make previews automatically?"
      : setting === "requireReview" ? "Turn off your approval?" : "Email guests automatically after approval?";
  const lines = setting === "autoPreviews"
    ? ["Every capture of this job gets its previews (a splat and AR models) once its photos are in the cloud, approved automatically.",
      "The captures already in the cloud get theirs now too (each one once).",
      "Switched off again, no preview is made unless you press Make previews now."]
    : setting === "processing"
    ? ["Its captures go to the processing computers, the oldest first, and every new one once its photos are in the cloud.",
      state.queuePaused ? "The whole queue is paused (Processing tab): nothing is processed until you resume it." : "",
      "Each model waits for your approval on the Review tab before any guest sees it."]
    : setting === "requireReview"
      ? ["Every model a processing computer reports for this job then goes to its guests' pages at once, with no one looking first.",
        state.autoSendOn ? "Automatic emails are on: those guests are also emailed at once." : "Guests are still emailed only when you press Send.",
        "Only for a job whose models you trust as they come. Processing cannot be on while it is."]
      : kind === "send_without_review"
        ? ["Your approval is off for this job: every model reported goes to its guests' pages at once, and with this on, every guest is emailed at once too.",
          "No one looks at a model before the guest has it in their inbox. An email cannot be taken back."]
        : ["When you approve a capture (or choose photos only), everyone in it who has not had it is emailed at once.",
          "Nothing is emailed before you approve it: your approval stays on."];
  return (
    <Dialog label={title} onClose={onClose}>
      <p className="rv-dialog-title">{title}</p>
      {(kind === "send_without_review" || needsSecond) && (
        <div className="rv-banner error" role="alert">Careful: guests get their email with no one checking their model first.</div>
      )}
      <div className="rv-dialog-lines">{lines.filter(Boolean).map((l) => <span key={l}>{l}</span>)}</div>
      {needsFirst && (
        <label className="rv-field">
          <span className="rv-label">Type &quot;{REVIEW_OFF_PHRASE}&quot; to go on</span>
          <input className="rv-input" value={first} autoComplete="off" onChange={(e) => setFirst(e.target.value)} />
        </label>
      )}
      {needsSecond && (
        <label className="rv-field">
          <span className="rv-label">And type &quot;{SEND_WITHOUT_REVIEW_PHRASE}&quot;</span>
          <input className="rv-input" value={second} autoComplete="off" onChange={(e) => setSecond(e.target.value)} />
        </label>
      )}
      <div className="rv-dialog-actions">
        <button type="button" className="rigv2-btn" onClick={onClose}>Back</button>
        <button type="button" className={`rigv2-btn ${kind === "confirm" ? "primary rv-primary" : "rv-danger"}`} disabled={busy || !ready}
          onClick={() => onConfirm({
            ...(needsFirst ? { confirmReviewOff: first } : {}),
            ...(needsSecond ? { confirmSendWithoutReview: second } : {}),
          })}>
          {busy ? "Saving…" : setting === "processing" || setting === "autoPreviews" ? "Switch on" : setting === "requireReview" ? "Turn approval off" : "Turn automatic emails on"}
        </button>
      </div>
    </Dialog>
  );
}

/**
 * The Previews block of a client job's card (pure props: exported for the
 * test): the switch, "Make previews now", and the recipients (a checkbox per
 * client contact address; Save sends the list).
 */
export function PreviewsSection({ state, busy = null, onToggle, onMake, onSaveRecipients, onSendLinks, retry = [] }) {
  const stored = state.previewRecipients || [];
  // After a press that did not reach everyone: the next press sends to those only (still recipients).
  const again = retry.filter((e) => stored.includes(e));
  // Preview links for this client (slice 4.4): off unless switched on for its organization, never for film and TV.
  const linksOff = state.previewLinks ? state.previewLinks.allowed === false : false;
  const offWhy = !linksOff ? "" : state.previewLinks.reason === "signed_in_only"
    ? "This client sees previews only signed in to Zelma (its sign-in or session policy): no emailed links."
    : state.previewLinks.reason === "no_org" ? "Preview links are off: this client has no Zelma organization yet."
      : state.previewLinks.reason === "org_not_active" ? "Preview links are off: this client's Zelma organization is not active."
        : "Preview links are off for this client. They are switched on for its organization first.";
  const choices = state.previewRecipientChoices || [];
  const [picked, setPicked] = useState(stored);
  const on = Boolean(state.autoPreviewsOn);
  // An address chosen before and no longer a contact's is still shown, so it can be taken off.
  const all = [...choices, ...stored.filter((e) => !choices.includes(e))];
  const dirty = JSON.stringify([...picked].sort()) !== JSON.stringify([...stored].sort());
  const flip = (e) => setPicked((p) => (p.includes(e) ? p.filter((x) => x !== e) : [...p, e]));
  // Slice 4.4: emailing the links asks once first (it writes to the client).
  const [asking, setAsking] = useState(false);
  return (
    <>
      <div className="rigv2-set-row">
        <span className="rigv2-set-label grow">Automate previews</span>
        <span className={`rigv2-set-state ${on ? "on" : ""}`}>{busy === "autoPreviews" ? "Saving…" : on ? "On" : "Off"}</span>
        <input type="checkbox" className="rigv2-toggle" aria-label="Automate previews" checked={on}
          disabled={Boolean(busy)} onChange={() => onToggle("autoPreviews")} />
      </div>
      <div className="rigv2-set-row tight">
        <span className="rigv2-set-hint grow">
          {on ? "Each capture gets a quick splat and AR preview once its photos are in the cloud, approved automatically. Previews you make by hand wait for your approval."
            : "Off: no previews are made unless you press Make previews now. Those wait for your approval."}
        </span>
        <button type="button" className="rigv2-set-sbtn" disabled={Boolean(busy)} onClick={onMake}>
          {busy === "makePreviews" ? "Starting…" : "Make previews now"}
        </button>
      </div>
      <div className="rigv2-set-row">
        <span className="rigv2-set-label grow">Who hears about previews</span>
      </div>
      <div className="rigv2-set-row tight">
        <span className="rigv2-set-hint grow">
          Chosen from this client&apos;s contacts. Send preview links emails each one a link to their previews on Zelma;
          opening it asks for a one-time code sent to the same address. Sending again replaces the old links.
        </span>
      </div>
      {linksOff && (
        <div className="rigv2-set-row tight">
          <span className="rigv2-set-hint grow warn" role="note">{offWhy}</span>
        </div>
      )}
      {all.length === 0 ? (
        <div className="rigv2-set-row tight">
          <span className="rigv2-set-hint grow">No contact of this client has an email yet: add one in the CRM.</span>
        </div>
      ) : (
        <div className="rigv2-set-row tight rigv2-set-recipients" role="group" aria-label="Who hears about previews">
          {all.map((e) => (
            <label key={e} className="rigv2-set-hint">
              <input type="checkbox" checked={picked.includes(e)} disabled={Boolean(busy) || (linksOff && !picked.includes(e))} onChange={() => flip(e)} /> {e}
              {!choices.includes(e) && " (no longer a contact)"}
            </label>
          ))}
          <button type="button" className="rigv2-set-sbtn" disabled={Boolean(busy) || !dirty}
            onClick={() => onSaveRecipients(picked.filter((e) => choices.includes(e)))}>
            {busy === "previewRecipients" ? "Saving…" : "Save"}
          </button>
        </div>
      )}
      {stored.length > 0 && onSendLinks && !linksOff && (
        <div className="rigv2-set-row tight">
          {asking ? (
            <>
              <span className="rigv2-set-hint grow">
                {again.length
                  ? `Email a preview link to the ${again.length === 1 ? "1 address" : `${again.length} addresses`} that did not get one (${again.join(", ")})?`
                  : `Email a preview link to ${stored.length === 1 ? stored[0] : `these ${stored.length} addresses`}?`}
              </span>
              <button type="button" className="rigv2-set-sbtn" disabled={Boolean(busy)} onClick={() => { setAsking(false); onSendLinks(again.length ? again : null); }}>Send</button>
              <button type="button" className="rigv2-set-sbtn" disabled={Boolean(busy)} onClick={() => setAsking(false)}>Cancel</button>
            </>
          ) : (
            <>
              <span className="rigv2-set-hint grow" />
              <button type="button" className="rigv2-set-sbtn" disabled={Boolean(busy) || dirty} onClick={() => setAsking(true)}>
                {busy === "sendPreviewLinks" ? "Sending…" : again.length ? "Send to the rest" : "Send preview links"}
              </button>
            </>
          )}
        </div>
      )}
    </>
  );
}

/** The card (pure props: exported for the test). */
export function JobAutomationView({
  state, busy = null, note = null, error = "", readAt = null, onToggle, onRefresh, onMakePreviews = () => {}, onSaveRecipients = () => {}, onSendPreviewLinks = null,
  retryLinks = [],
}) {
  return (
    <div className="rigv2-set-card rigv2-set-auto">
      <div className="rigv2-set-head">
        <span className="rigv2-set-title">Automation</span>
        {onRefresh && <button type="button" className="rigv2-set-sbtn rigv2-set-push" disabled={Boolean(busy)} onClick={onRefresh}>Refresh</button>}
      </div>
      {error && (
        <div className="rigv2-set-row">
          <span className="rigv2-set-warn" role="alert">
            {error} What is shown is as read{readAt ? ` at ${shortTime(readAt)}` : " before"}; it may have changed since.
          </span>
        </div>
      )}
      <div className="rigv2-set-desc">
        What happens to this job&apos;s captures without you. Shown as the processing queue and guest delivery read them now;
        every change is made by the server and recorded with your name.
      </div>
      {SETTINGS.map((s) => {
        const on = valueOf(state, s.id);
        const blocked = blockedReason(state, s.id);
        return (
          <React.Fragment key={s.id}>
            <div className="rigv2-set-row">
              <span className="rigv2-set-label grow">{s.label}</span>
              <span className={`rigv2-set-state ${on ? "on" : ""}`}>{busy === s.id ? "Saving…" : on ? "On" : "Off"}</span>
              <input type="checkbox" className="rigv2-toggle" aria-label={s.label} checked={on}
                disabled={Boolean(busy) || Boolean(blocked)} onChange={() => onToggle(s.id)} />
            </div>
            <div className="rigv2-set-row tight">
              <span className="rigv2-set-hint grow">{s.hint}</span>
              {blocked && <span className="rigv2-set-warn">{blocked}</span>}
              {s.id === "processing" && on && state.queuePaused && (
                <span className="rigv2-set-warn">The whole queue is paused (Processing tab): nothing is processed until you resume it.</span>
              )}
              {s.id === "processing" && on && state.problem && (
                <span className="rigv2-set-warn" role="alert">
                  Careful: processing is on, but the job&apos;s approval switch is off (changed outside this screen).
                  While processing is on, the Suite still holds every model for your approval; once you switch processing off, the switch counts. Turn approval on to match.
                </span>
              )}
              {s.id === "autoSend" && on && !state.reviewOn && !state.processing && (
                <span className="rigv2-set-warn" role="alert">Careful: with approval off and automatic emails on, guests are emailed models no one has looked at.</span>
              )}
            </div>
          </React.Fragment>
        );
      })}
      {state.kind === "client" && (
        <PreviewsSection key={(state.previewRecipients || []).join(",")} state={state} busy={busy} onToggle={onToggle}
          onMake={onMakePreviews} onSaveRecipients={onSaveRecipients} onSendLinks={onSendPreviewLinks} retry={retryLinks} />
      )}
      {note && <div className="rigv2-set-row"><span className={note.kind === "error" ? "rigv2-set-warn" : "rigv2-set-hint"} role="status">{note.text}</span></div>}
      {state.history?.length > 0 && (
        <details className="rigv2-set-history">
          <summary className="rigv2-set-hint">Last changes</summary>
          <ul>{state.history.map((h) => <li key={`${h.at}-${h.setting}-${h.to}`} className="rigv2-set-hint">{historyLine(h)}</li>)}</ul>
        </details>
      )}
    </div>
  );
}

/**
 * The recipient choices read every contact on the server: a read asks for them
 * when told to (Refresh, a retry) or while the card has none yet; the 30 s
 * refresh otherwise does not. Exported for the test.
 */
export const choicesWanted = (opts, have) => opts?.choices === true || !have;
/**
 * A read's answer: shown only while the card is live and it is the newest read; only a shown answer
 * counts as the card having the choices (an answer dropped for a newer read never does). Exported for the test.
 */
export const acceptRead = (live, mine, current, s, have) =>
  (live && mine === current ?
    // A job that is not a client job has none to read: asked again each time (cheap), so a job made client gets them.
    { accept: true, have: have || (Array.isArray(s?.previewRecipientChoices) && s?.kind === "client") } : { accept: false, have });
/** An answer laid over the card: the choices it brings, else the ones the card has. Exported for the test. */
export const withChoices = (s, prev) => ({ ...s, previewRecipientChoices: s.previewRecipientChoices ?? prev?.previewRecipientChoices });

export default function JobAutomation({ jobId }) {
  const [state, setState] = useState(null);
  const [error, setError] = useState("");
  const [readAt, setReadAt] = useState(null);
  const [busy, setBusy] = useState(null);
  const [note, setNote] = useState(null);
  const [retryLinks, setRetryLinks] = useState([]);
  const [dialog, setDialog] = useState(null);
  const live = useRef(true);
  // A late answer never overwrites a newer one (a read that began before a change, say).
  const seq = useRef(0);
  // Where the last "Make previews now" (or the switch-on pass) stopped, when it said there are more.
  const resumeAfter = useRef(null);

  // Whether this card has the choices yet: a read asks for them until one brings them.
  const haveChoices = useRef(false);
  const load = useCallback(async (opts = {}) => {
    const mine = ++seq.current;
    try {
      const s = await rigv2.getJobAutomation({ jobId, choices: choicesWanted(opts, haveChoices.current) });
      const r = acceptRead(live.current, mine, seq.current, s, haveChoices.current);
      haveChoices.current = r.have;
      if (r.accept) { setState((prev) => withChoices(s, prev)); setError(""); setReadAt(Date.now()); }
    } catch (e) {
      if (live.current && mine === seq.current) setError(`Could not read the automation switches (${e?.message || "no answer"}).`);
    }
  }, [jobId]);
  // Now, and every 30 seconds while the page is on screen: a capture still
  // being processed ends, another admin changes a switch (and on coming back).
  useEffect(() => {
    live.current = true;
    setState(null);
    load();
    const hidden = () => typeof document !== "undefined" && document.visibilityState === "hidden";
    const t = setInterval(() => { if (!hidden()) load(); }, 30_000);
    const onVisible = () => { if (!hidden()) load(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      live.current = false;
      clearInterval(t);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [load]);

  const change = async (setting, value, confirm = {}) => {
    setDialog(null);
    setBusy(setting);
    setNote(null);
    try {
      const s = await rigv2.setJobAutomation({ jobId, setting, value, ...confirm });
      if (!live.current) return;
      seq.current += 1;
      setState((prev) => withChoices(s, prev));
      setError("");
      setReadAt(Date.now());
      if (s.previews) resumeAfter.current = s.previews.more ? s.previews.after ?? null : null;
      setNote({ kind: "ok", text: s.changed === false ? "Already so: nothing changed." : `Saved.${s.previews ? ` ${previewsLine(s.previews)}` : ""}` });
    } catch (e) {
      if (live.current) setNote({ kind: "error", text: e?.message || "That did not work. Try again." });
      load();
    } finally {
      if (live.current) setBusy(null);
    }
  };
  const makePreviews = async () => {
    setBusy("makePreviews");
    setNote(null);
    try {
      const r = await rigv2.makePreviews({ jobId, after: resumeAfter.current });
      if (live.current) {
        resumeAfter.current = r.more ? r.after ?? null : null;
        setNote({ kind: "ok", text: `${previewsLine(r)} They wait for your approval.` });
      }
    } catch (e) {
      if (live.current) setNote({ kind: "error", text: e?.message || "That did not work. Try again." });
    } finally {
      if (live.current) setBusy(null);
    }
  };
  const sendLinks = async (emails = null) => {
    setBusy("sendPreviewLinks");
    setNote(null);
    try {
      const r = await rigv2.sendPreviewLinks({ jobId, ...(emails ? { emails } : {}) });
      if (live.current) {
        const missed = Array.isArray(r.notSent) ? r.notSent : [];
        setRetryLinks(missed);
        setNote(missed.length
          ? { kind: "error", text: `Sent ${r.sent}; ${missed.length} did not get a link (${r.to.filter((t) => !t.sent).map((t) => t.emailMasked).join(", ")}). Press Send to the rest to send to those only.` }
          : { kind: "ok", text: `Preview links sent to ${r.sent === 1 ? "1 address" : `${r.sent} addresses`}.` });
      }
    } catch (e) {
      if (live.current) setNote({ kind: "error", text: e?.message || "That did not work. Try again." });
    } finally {
      if (live.current) setBusy(null);
    }
  };
  const saveRecipients = async (emails) => {
    setBusy("previewRecipients");
    setNote(null);
    try {
      const s = await rigv2.setJobAutomation({ jobId, setting: "previewRecipients", emails });
      if (!live.current) return;
      seq.current += 1;
      if (Array.isArray(s.previewRecipientChoices)) haveChoices.current = true;
      setState((prev) => withChoices(s, prev));
      setReadAt(Date.now());
      setNote({ kind: "ok", text: s.changed === false ? "Already so: nothing changed." : "Saved." });
    } catch (e) {
      if (live.current) setNote({ kind: "error", text: e?.message || "That did not work. Try again." });
      load();
    } finally {
      if (live.current) setBusy(null);
    }
  };
  const onToggle = (id) => {
    if (!state) return;
    const ask = askFor(state, id);
    const next = !valueOf(state, id);
    if (ask) setDialog({ kind: ask, setting: id, value: next });
    else change(id, next);
  };

  if (!state) {
    return (
      <div className="rigv2-set-card">
        <div className="rigv2-set-head"><span className="rigv2-set-title">Automation</span></div>
        <div className="rigv2-set-desc">{error || "Loading…"}</div>
        {error && <button type="button" className="rigv2-set-sbtn" onClick={() => load({ choices: true })}>Try again</button>}
      </div>
    );
  }
  return (
    <>
      <JobAutomationView state={state} busy={busy} note={note} error={error} readAt={readAt} onToggle={onToggle} onRefresh={() => load({ choices: true })}
        onMakePreviews={makePreviews} onSaveRecipients={saveRecipients} onSendPreviewLinks={sendLinks} retryLinks={retryLinks} />
      {dialog && (
        // .rigv2-review: the Review tab's dialog look (its buttons, banner and colours).
        <div className="rigv2-review">
          <AutomationDialog kind={dialog.kind} setting={dialog.setting} state={state} busy={busy === dialog.setting}
            onConfirm={(confirm) => change(dialog.setting, dialog.value, confirm)} onClose={() => setDialog(null)} />
        </div>
      )}
    </>
  );
}
